import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { bestForeground } from "@/lib/wcag-contrast";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { Check, ArrowRight, ArrowLeft, Calendar as CalendarIcon, Clock, User, CheckCircle2, Loader2, Euro, Sun, Moon, Settings2, UserCheck } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, addDays, isBefore, startOfDay, getDay, isSameDay, parseISO } from "date-fns";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { supabase } from "@/integrations/supabase/client";
import slotifyLogoSrc from "@/assets/slotify-logo.svg";
import AddToCalendar from "@/components/AddToCalendar";
import { useTheme } from "next-themes";

interface ServiceOption {
  id: string;
  name: string;
  price: number;
  duration: number;
}

interface UpsellOption {
  id: string;
  name: string;
  price: number;
  duration: number;
}

interface Upsell {
  id: string;
  name: string;
  required: boolean;
  option_selection_mode: string;
  options: UpsellOption[];
}

interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: number;
  options: ServiceOption[];
  option_required: boolean;
  option_selection_mode: string;
}

interface OpeningHour {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

interface BlockedTime {
  start_date: string;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  type: string;
  employee_id?: string | null;
}

interface ExistingReservation {
  date: string;
  duration: number;
  employee_id?: string | null;
}

interface ShopEmployee {
  id: string;
  name: string;
  image_url: string | null;
}

interface CheckoutFieldConfig {
  id: string;
  label: string;
  type: string;
  required: boolean;
  placeholder: string;
  options: string;
  sort_order: number;
}

interface Organisation {
  id: string;
  name: string;
  logo_light_url: string | null;
  logo_dark_url: string | null;
  logo_size: string;
  subscription_status: string;
  trial_ends_at: string;
  shop_primary_color: string | null;
  shop_primary_color_dark: string | null;
  shop_button_text_color: string | null;
  shop_button_text_color_dark: string | null;
  shop_text_color: string | null;
  shop_text_color_dark: string | null;
  shop_bg_color: string | null;
  shop_bg_color_dark: string | null;
  shop_card_bg_color: string | null;
  shop_card_bg_color_dark: string | null;
  shop_font_family: string;
  shop_font_size: string;
  booking_min_lead_time: number;
  booking_max_advance_time: number | null;
  booking_min_lead_snap_to_day: boolean;
  booking_max_advance_snap_to_day: boolean;
  booking_slot_interval: number;
}

const ShopBooking = () => {
  const { shopId } = useParams();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get("embed") === "true";
  const { t, language, setLanguage } = useLanguage();
  const { resolvedTheme, setTheme } = useTheme();

  // Apply ?lang and ?theme query params on mount
  useEffect(() => {
    const langParam = searchParams.get("lang");
    if (langParam && ["en", "nl", "fr", "de"].includes(langParam) && langParam !== language) {
      setLanguage(langParam as any);
    }
    const themeParam = searchParams.get("theme");
    const storedTheme = localStorage.getItem("theme");
    if (themeParam && ["light", "dark"].includes(themeParam) && !storedTheme) {
      setTheme(themeParam);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [shopUnavailable, setShopUnavailable] = useState(false);
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [openingHours, setOpeningHours] = useState<OpeningHour[]>([]);
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>([]);
  const [existingReservations, setExistingReservations] = useState<ExistingReservation[]>([]);
  const [slotLocks, setSlotLocks] = useState<ExistingReservation[]>([]);
  const [lockToken, setLockToken] = useState<string | null>(null);
  const lockTokenRef = useRef<string | null>(null);
  // Keep ref in sync with state
  useEffect(() => { lockTokenRef.current = lockToken; }, [lockToken]);
  const [checkoutFields, setCheckoutFields] = useState<CheckoutFieldConfig[]>([]);
  const [serviceUpsells, setServiceUpsells] = useState<Upsell[]>([]);
  const [selectedUpsellOptions, setSelectedUpsellOptions] = useState<Record<string, string[]>>({});
  const [selfServiceEnabled, setSelfServiceEnabled] = useState(false);
  const [selfServiceThankYou, setSelfServiceThankYou] = useState(false);
  const [payAtOrderEnabled, setPayAtOrderEnabled] = useState(false);
  const [employeesEnabled, setEmployeesEnabled] = useState(false);
  const [allowCustomerSelection, setAllowCustomerSelection] = useState(false);
  const [allowNoPreference, setAllowNoPreference] = useState(true);
  const [employeeBeforeService, setEmployeeBeforeService] = useState(false);
  const [shopEmployees, setShopEmployees] = useState<ShopEmployee[]>([]);
  const [employeeServiceMap, setEmployeeServiceMap] = useState<Record<string, string[]>>({});
  const [employeeWorkingHours, setEmployeeWorkingHours] = useState<Record<string, { day_of_week: number; is_open: boolean; open_time: string; close_time: string }[]>>({});
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null); // null = no preference
  const fetchAvailability = useCallback(async () => {
    if (!shopId) return;
    const [blockedRes, reservationsRes, locksRes] = await Promise.all([
      supabase
        .from("blocked_times")
        .select("start_date, end_date, start_time, end_time, type, employee_id")
        .eq("organisation_id", shopId)
        .eq("active", true),
      supabase
        .from("reservations")
        .select("date, duration, employee_id")
        .eq("organisation_id", shopId)
        .in("status", ["pending", "confirmed", "awaiting_payment"]),
      supabase
        .from("slot_locks")
        .select("slot_date, duration, lock_token")
        .eq("organisation_id", shopId)
        .gt("expires_at", new Date().toISOString()),
    ]);
    setBlockedTimes((blockedRes.data || []).map((bt: any) => ({ ...bt, employee_id: bt.employee_id || null })));
    setExistingReservations(
      (reservationsRes.data || []).map((r: any) => ({ date: r.date, duration: r.duration, employee_id: r.employee_id || null }))
    );
    // Filter out our own lock from the locks list (use ref to avoid stale closure)
    setSlotLocks(
      (locksRes.data || [])
        .filter((l: any) => l.lock_token !== lockTokenRef.current)
        .map((l: any) => ({ date: l.slot_date, duration: l.duration }))
    );
  }, [shopId]);

  useEffect(() => {
    if (!shopId) return;
    const fetchShopData = async () => {
      setLoading(true);
      // Fetch organisation (via safe RPC that only returns public fields)
      const { data: org } = await supabase
        .rpc("get_public_org_info", { _org_id: shopId })
        .single();

      if (!org) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      // Check subscription status
      const isTrialing = org.subscription_status === "trialing" && new Date(org.trial_ends_at) > new Date();
      const isActive = org.subscription_status === "active";
      
      // For cancelled/failed orgs, check if still within paid period via next_payment_date
      let cancelledButActive = false;
      if (org.subscription_status === "cancelled" || org.subscription_status === "failed") {
        const { data: sub } = await supabase
          .from("subscriptions")
          .select("next_payment_date")
          .eq("organisation_id", shopId)
          .maybeSingle();
        cancelledButActive = !!sub?.next_payment_date && new Date(sub.next_payment_date) > new Date();
      }

      if (!isTrialing && !isActive && !cancelledButActive) {
        setShopUnavailable(true);
        setOrganisation(org);
        setLoading(false);
        return;
      }
      setOrganisation(org);

      // Fetch services, opening hours, blocked times in parallel
      const [servicesRes, hoursRes, blockedRes, reservationsRes, checkoutFieldsRes, locksRes] = await Promise.all([
        supabase
          .from("services")
          .select("id, name, description, price, duration, option_required, option_selection_mode, service_options(id, name, price, duration)")
          .eq("organisation_id", shopId)
          .eq("active", true)
          .order("sort_order", { ascending: true }),
        supabase
          .from("opening_hours")
          .select("day_of_week, is_open, open_time, close_time")
          .eq("organisation_id", shopId),
        supabase
          .from("blocked_times")
          .select("start_date, end_date, start_time, end_time, type")
          .eq("organisation_id", shopId)
          .eq("active", true),
        supabase
          .from("reservations")
          .select("date, duration")
          .eq("organisation_id", shopId)
          .in("status", ["pending", "confirmed", "awaiting_payment"]),
        supabase
          .from("checkout_fields")
          .select("id, label, type, required, placeholder, options, sort_order")
          .eq("organisation_id", shopId)
          .order("sort_order", { ascending: true }),
        supabase
          .from("slot_locks")
          .select("slot_date, duration, lock_token")
          .eq("organisation_id", shopId)
          .gt("expires_at", new Date().toISOString()),
      ]);

      const mappedServices: Service[] = (servicesRes.data || []).map((s: any) => ({
        id: s.id,
        name: s.name,
        description: s.description || "",
        price: Number(s.price),
        duration: s.duration,
        option_required: s.option_required || false,
        option_selection_mode: s.option_selection_mode || "multiple",
        options: (s.service_options || []).map((o: any) => ({
          id: o.id,
          name: o.name,
          price: Number(o.price),
          duration: o.duration,
        })),
      }));

      setServices(mappedServices);
      setOpeningHours(hoursRes.data || []);
      setBlockedTimes(blockedRes.data || []);
      setExistingReservations(
        (reservationsRes.data || []).map((r: any) => ({ date: r.date, duration: r.duration }))
      );
      setSlotLocks(
        (locksRes.data || [])
          .filter((l: any) => l.lock_token !== lockToken)
          .map((l: any) => ({ date: l.slot_date, duration: l.duration }))
      );
      setCheckoutFields(
        (checkoutFieldsRes.data || []).map((f: any) => ({
          id: f.id,
          label: f.label,
          type: f.type,
          required: f.required,
          placeholder: f.placeholder || "",
          options: f.options || "",
          sort_order: f.sort_order,
        }))
      );
      // Check self-service extension (via safe RPC that strips secrets)
      const { data: extData } = await supabase
        .rpc("get_public_extension_info", { _organisation_id: shopId, _extension_id: "self-service" })
        .maybeSingle();
      if (extData && extData.enabled) {
        const s = (extData.settings as any) || {};
        if (s.show_in_shop !== false) {
          setSelfServiceEnabled(true);
        }
        if (s.show_on_thank_you !== false) {
          setSelfServiceThankYou(true);
        }
      }

      // Check pay-at-order extension
      const { data: paoData } = await supabase
        .rpc("get_public_extension_info", { _organisation_id: shopId, _extension_id: "pay-at-order" })
        .maybeSingle();
      if (paoData && paoData.enabled) {
        setPayAtOrderEnabled(true);
      }

      // Check employees extension
      const { data: empExtData } = await supabase
        .rpc("get_public_extension_info", { _organisation_id: shopId, _extension_id: "employees" })
        .maybeSingle();
      if (empExtData && empExtData.enabled) {
        const empSettings = (empExtData.settings as any) || {};
        if (empSettings.allow_customer_selection !== false) {
          setEmployeesEnabled(true);
          setAllowCustomerSelection(true);
          setAllowNoPreference(empSettings.allow_no_preference !== false);
          setEmployeeBeforeService(empSettings.employee_step_before_service === true);
          // Fetch employees and their service assignments
          const [empRes, empSvcRes, empHoursRes] = await Promise.all([
            supabase.from("employees").select("id, name, image_url").eq("organisation_id", shopId).eq("active", true).order("sort_order"),
            supabase.from("employee_services").select("employee_id, service_id"),
            supabase.from("employee_working_hours").select("employee_id, day_of_week, is_open, open_time, close_time"),
          ]);
          setShopEmployees((empRes.data as any) || []);
          const svcMap: Record<string, string[]> = {};
          (empSvcRes.data || []).forEach((row: any) => {
            if (!svcMap[row.employee_id]) svcMap[row.employee_id] = [];
            svcMap[row.employee_id].push(row.service_id);
          });
          setEmployeeServiceMap(svcMap);
          const hoursMap: Record<string, { day_of_week: number; is_open: boolean; open_time: string; close_time: string }[]> = {};
          (empHoursRes.data || []).forEach((row: any) => {
            if (!hoursMap[row.employee_id]) hoursMap[row.employee_id] = [];
            hoursMap[row.employee_id].push({ day_of_week: row.day_of_week, is_open: row.is_open, open_time: row.open_time, close_time: row.close_time });
          });
          setEmployeeWorkingHours(hoursMap);
        }
      }

      setLoading(false);
    };
    fetchShopData();
  }, [shopId]);

  // Realtime: refetch availability when reservations or blocked_times change
  useEffect(() => {
    if (!shopId) return;
    const channel = supabase
      .channel(`shop-availability-${shopId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "reservations", filter: `organisation_id=eq.${shopId}` }, () => {
        fetchAvailability();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "blocked_times", filter: `organisation_id=eq.${shopId}` }, () => {
        fetchAvailability();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "slot_locks" }, () => {
        fetchAvailability();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [shopId, fetchAvailability]);

  // Load Google Font when organisation data is available
  useEffect(() => {
    if (!organisation?.shop_font_family || organisation.shop_font_family === "Inter") return;
    const fontName = organisation.shop_font_family.replace(/ /g, "+");
    const linkId = `shop-google-font`;
    const existing = document.getElementById(linkId);
    if (existing) existing.remove();
    const link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, [organisation?.shop_font_family]);

  const jsToDbDay = (jsDay: number) => (jsDay === 0 ? 6 : jsDay - 1);
  const jsToEmployeeDay = (jsDay: number) => (jsDay === 0 ? 7 : jsDay);

  // Build helpers from fetched data
  const closedDays = useMemo(() => {
    return openingHours.filter((h) => !h.is_open).map((h) => h.day_of_week);
  }, [openingHours]);

  // Full-day blocked dates (no start_time means entire day is blocked)
  const fullDayBlockedDates = useMemo(() => {
    const dates: Date[] = [];
    const parseLocalDate = (d: string) => {
      const [y, m, day] = d.split("-").map(Number);
      return new Date(y, m - 1, day);
    };
    blockedTimes.forEach((bt) => {
      // For datetime_range with times: middle days are fully blocked
      if (bt.type === "datetime_range" && bt.start_time && bt.end_time && bt.end_date && bt.start_date !== bt.end_date) {
        let cur = addDays(parseLocalDate(bt.start_date), 1);
        const end = addDays(parseLocalDate(bt.end_date), -1);
        while (cur <= end) {
          dates.push(new Date(cur));
          cur = addDays(cur, 1);
        }
        return;
      }
      // Only block full days if no specific time range
      if (bt.start_time && bt.end_time) return;
      if (bt.type === "single_day" || !bt.end_date) {
        dates.push(parseLocalDate(bt.start_date));
      } else {
        let cur = parseLocalDate(bt.start_date);
        const end = parseLocalDate(bt.end_date);
        while (cur <= end) {
          dates.push(new Date(cur));
          cur = addDays(cur, 1);
        }
      }
    });
    return dates;
  }, [blockedTimes]);

  // Check if a timeslot is blocked by a time-specific block for a given employee (or org-wide)
  const isSlotBlockedForEmployee = (date: Date, slotStart: number, slotEnd: number, empId: string | null): boolean => {
    return blockedTimes.some((bt) => {
      if (!bt.start_time || !bt.end_time) return false;
      // Employee filter: org-wide blocks (no employee_id) apply to everyone;
      // employee-specific blocks only apply to that employee
      if (bt.employee_id && empId && bt.employee_id !== empId) return false;
      // If this block is for a specific employee but we're checking org-wide (empId=null), skip it
      if (bt.employee_id && !empId) return false;

      const [sy, sm, sd] = bt.start_date.split("-").map(Number);
      const btStartDate = new Date(sy, sm - 1, sd);
      const [ey, em, ed] = bt.end_date ? bt.end_date.split("-").map(Number) : [sy, sm, sd];
      const btEndDate = new Date(ey, em - 1, ed);
      const dateDay = startOfDay(date);
      if (dateDay < btStartDate || dateDay > btEndDate) return false;

      const [bsH, bsM] = bt.start_time.split(":").map(Number);
      const [beH, beM] = bt.end_time.split(":").map(Number);
      const blockStart = bsH * 60 + bsM;
      const blockEnd = beH * 60 + beM;

      if (bt.type === "daily_time_range" && bt.end_date) {
        if (blockStart >= blockEnd) return false;
        return slotStart < blockEnd && slotEnd > blockStart;
      }

      if (bt.type === "datetime_range" && bt.end_date && bt.start_date !== bt.end_date) {
        const isFirstDay = dateDay.getTime() === btStartDate.getTime();
        const isLastDay = dateDay.getTime() === btEndDate.getTime();
        const isMiddleDay = dateDay > btStartDate && dateDay < btEndDate;
        if (isMiddleDay) return true;
        if (isFirstDay && isLastDay) {
          if (blockStart >= blockEnd) return true;
          return slotStart < blockEnd && slotEnd > blockStart;
        }
        if (isFirstDay) return slotEnd > blockStart;
        if (isLastDay) return slotStart < blockEnd;
        return false;
      }

      if (blockStart >= blockEnd) return false;
      return slotStart < blockEnd && slotEnd > blockStart;
    });
  };

  // Check if an employee is NOT working during a given slot (based on employee_working_hours)
  // Returns true if the employee has custom hours AND is not available for this slot
  const isEmployeeOffDuty = (date: Date, slotStart: number, slotEnd: number, empId: string): boolean => {
    const hours = employeeWorkingHours[empId];
    if (!hours || hours.length === 0) return false; // No custom hours = follows org hours
    const dayOfWeek = jsToEmployeeDay(getDay(date));
    const dayHours = hours.find((h) => h.day_of_week === dayOfWeek);
    if (!dayHours) return false; // No entry for this day = follows org hours
    if (!dayHours.is_open) return true; // Employee marked as free/off on this day
    // Employee has custom hours for this day — check if slot falls within their working window
    const [oH, oM] = dayHours.open_time.split(":").map(Number);
    const [cH, cM] = dayHours.close_time.split(":").map(Number);
    const empOpen = oH * 60 + oM;
    const empClose = cH * 60 + cM;
    // Slot must fit entirely within employee's working window
    return slotStart < empOpen || slotEnd > empClose;
  };

  // Legacy wrapper for non-employee mode
  const isSlotBlocked = (date: Date, slotStart: number, slotEnd: number): boolean => {
    return isSlotBlockedForEmployee(date, slotStart, slotEnd, null);
  };

  // Check if a timeslot overlaps with an existing reservation for a given employee
  const isSlotBookedForEmployee = (date: Date, slotStart: number, slotEnd: number, empId: string | null): boolean => {
    const resBooked = existingReservations.some((r) => {
      // If checking for a specific employee, only count reservations for that employee
      if (empId && r.employee_id && r.employee_id !== empId) return false;
      // If checking org-wide (no employee), only count reservations without an employee
      if (!empId && r.employee_id) return false;
      const rDate = parseISO(r.date);
      if (!isSameDay(rDate, date)) return false;
      const rStart = rDate.getHours() * 60 + rDate.getMinutes();
      const rEnd = rStart + r.duration;
      return slotStart < rEnd && slotEnd > rStart;
    });
    if (resBooked) return true;
    return slotLocks.some((l) => {
      const lDate = parseISO(l.date);
      if (!isSameDay(lDate, date)) return false;
      const lStart = lDate.getHours() * 60 + lDate.getMinutes();
      const lEnd = lStart + l.duration;
      return slotStart < lEnd && slotEnd > lStart;
    });
  };

  // Legacy wrapper
  const isSlotBooked = (date: Date, slotStart: number, slotEnd: number): boolean => {
    if (!employeesEnabled || !selectedEmployee) {
      // If employees enabled with "no preference", slot is only unavailable if ALL eligible employees are busy/blocked/off
      if (employeesEnabled && !selectedEmployee && selectedService) {
        const eligibleEmployees = shopEmployees.filter((e) => {
          const svcIds = employeeServiceMap[e.id] || [];
          return svcIds.includes(selectedService.id);
        });
        if (eligibleEmployees.length > 0) {
          return eligibleEmployees.every((emp) =>
            isSlotBookedForEmployee(date, slotStart, slotEnd, emp.id) ||
            isSlotBlockedForEmployee(date, slotStart, slotEnd, emp.id) ||
            isEmployeeOffDuty(date, slotStart, slotEnd, emp.id)
          );
        }
      }
      return isSlotBookedForEmployee(date, slotStart, slotEnd, null);
    }
    return isSlotBookedForEmployee(date, slotStart, slotEnd, selectedEmployee) ||
           isSlotBlockedForEmployee(date, slotStart, slotEnd, selectedEmployee) ||
           isEmployeeOffDuty(date, slotStart, slotEnd, selectedEmployee);
  };

  const getAvailableSlots = (date: Date, duration: number): string[] => {
    // Normalize to local midnight to avoid UTC-vs-local timezone issues
    // (react-day-picker passes dates as UTC midnight which equals "previous day 11pm" in UTC+ zones)
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayOfWeek = jsToDbDay(getDay(localDate));
    const dayHours = openingHours.find((h) => h.day_of_week === dayOfWeek && h.is_open);
    if (!dayHours) return [];
    const openH = parseInt(dayHours.open_time.split(":")[0]);
    const openM = parseInt(dayHours.open_time.split(":")[1]);
    const closeH = parseInt(dayHours.close_time.split(":")[0]);
    const closeM = parseInt(dayHours.close_time.split(":")[1]);
    const closeMinutes = closeH * 60 + closeM;

    const now = new Date();
    const minLeadMinutes = organisation?.booking_min_lead_time || 0;
    const snapMinToDay = organisation?.booking_min_lead_snap_to_day || false;
    const maxAdvanceMinutes = (organisation?.booking_max_advance_time != null && organisation.booking_max_advance_time > 0) ? organisation.booking_max_advance_time : null;
    const snapMaxToDay = organisation?.booking_max_advance_snap_to_day || false;

    // Calculate the earliest bookable moment
    let earliestBookable = new Date(now.getTime() + minLeadMinutes * 60 * 1000);
    if (snapMinToDay && minLeadMinutes > 0) {
      // If the lead time crosses into a new day, snap to start of that day
      if (startOfDay(earliestBookable).getTime() > startOfDay(now).getTime()) {
        earliestBookable = startOfDay(earliestBookable);
      }
    }

    // Calculate the latest bookable moment
    let latestBookable: Date | null = null;
    if (maxAdvanceMinutes) {
      latestBookable = new Date(now.getTime() + maxAdvanceMinutes * 60 * 1000);
      if (snapMaxToDay) {
        // Snap to end of that day (23:59:59)
        latestBookable = new Date(startOfDay(latestBookable).getTime() + 24 * 60 * 60 * 1000 - 1);
      }
    }

    const slotStep = organisation?.booking_slot_interval || 30;
    const slots: string[] = [];
    let m = openH * 60 + openM;
    while (m + duration <= closeMinutes) {
      const slotEnd = m + duration;

      // Build full datetime for this slot using local midnight as base
      const slotDate = new Date(localDate);
      slotDate.setHours(Math.floor(m / 60), m % 60, 0, 0);

      // Skip slots that are too soon
      if (slotDate < earliestBookable) { m += slotStep; continue; }
      // Skip slots that are too far in the future
      if (latestBookable && slotDate > latestBookable) { m += slotStep; continue; }

      if (!isSlotBlocked(localDate, m, slotEnd) && !isSlotBooked(localDate, m, slotEnd)) {
        const h = Math.floor(m / 60);
        const min = m % 60;
        slots.push(`${h.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`);
      }
      m += slotStep;
    }
    return slots;
  };

  const isDateDisabled = (date: Date) => {
    // Normalize to local midnight to avoid UTC-vs-local timezone issues
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = startOfDay(new Date());
    if (isBefore(localDate, today)) return true;
    const dow = jsToDbDay(getDay(localDate));
    if (closedDays.includes(dow)) return true;
    if (fullDayBlockedDates.some((d) => d.getTime() === localDate.getTime())) return true;

    // If max advance is set (non-null and > 0), disable dates beyond it
    if (organisation?.booking_max_advance_time != null && organisation.booking_max_advance_time > 0) {
      let maxDate = new Date(new Date().getTime() + organisation.booking_max_advance_time * 60 * 1000);
      if (organisation.booking_max_advance_snap_to_day) {
        maxDate = new Date(startOfDay(maxDate).getTime() + 24 * 60 * 60 * 1000 - 1);
      }
      if (localDate > startOfDay(addDays(maxDate, 1))) return true;
    }

    // Also disable if all slots are taken
    if (selectedService) {
      const slots = getAvailableSlots(localDate, totalDuration);
      if (slots.length === 0) return true;
    }
    return false;
  };

  const getFirstAvailableDate = () => {
    let candidate = startOfDay(new Date());
    for (let i = 0; i < 365; i++) {
      if (!isDateDisabled(candidate)) return candidate;
      candidate = addDays(candidate, 1);
    }
    return undefined;
  };

  // Dynamic steps: depends on employeeBeforeService setting
  const hasEmployeeStep = employeesEnabled && allowCustomerSelection;
  const empBefore = hasEmployeeStep && employeeBeforeService;
  const employeeStepOffset = hasEmployeeStep ? 1 : 0;

  const STEP_EMPLOYEE = hasEmployeeStep ? (empBefore ? 1 : 2) : -1;
  const STEP_SERVICE = empBefore ? 2 : 1;
  const upsellCount = serviceUpsells.length;
  const upsellBase = (empBefore ? 3 : (2 + employeeStepOffset));
  const STEP_DATETIME = upsellBase + upsellCount;
  const STEP_DETAILS = STEP_DATETIME + 1;
  const STEP_CONFIRMED = STEP_DETAILS + 1;

  // eligibleEmployees moved below after state declarations

  const steps = [
    ...(empBefore ? [{ id: STEP_EMPLOYEE, label: t("shop.step.employee"), icon: UserCheck }] : []),
    { id: STEP_SERVICE, label: t("shop.step.service"), icon: Check },
    ...(!empBefore && hasEmployeeStep ? [{ id: STEP_EMPLOYEE, label: t("shop.step.employee"), icon: UserCheck }] : []),
    ...serviceUpsells.map((u, i) => ({
      id: upsellBase + i,
      label: u.name,
      icon: Check,
    })),
    { id: STEP_DATETIME, label: t("shop.step.datetime"), icon: CalendarIcon },
    { id: STEP_DETAILS, label: t("shop.step.details"), icon: User },
    { id: STEP_CONFIRMED, label: t("shop.step.confirmed"), icon: CheckCircle2 },
  ];

  // Persist booking state in sessionStorage
  const storageKey = `shop-booking-${shopId}`;
  const getSaved = () => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const saved = useMemo(getSaved, [storageKey]);

  const [step, setStep] = useState(() => saved?.step || 1);
  const stepsContainerRef = useRef<HTMLDivElement>(null);
  const activeStepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeStepRef.current && stepsContainerRef.current) {
      activeStepRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [step]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [pendingServiceId, setPendingServiceId] = useState<string | null>(() => saved?.serviceId || null);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(() => saved?.options || []);

  // Employees eligible for the selected service
  const eligibleEmployees = useMemo(() => {
    if (!selectedService || !employeesEnabled) return [];
    return shopEmployees.filter((e) => {
      const svcIds = employeeServiceMap[e.id] || [];
      return svcIds.includes(selectedService.id);
    });
  }, [selectedService, shopEmployees, employeeServiceMap, employeesEnabled]);

  // When employee is selected before service, filter services to those the employee can do
  const displayedServices = useMemo(() => {
    if (!empBefore || !selectedEmployee) return services;
    const empSvcIds = employeeServiceMap[selectedEmployee] || [];
    return services.filter((s) => empSvcIds.includes(s.id));
  }, [empBefore, selectedEmployee, services, employeeServiceMap]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => saved?.date ? new Date(saved.date) : undefined);
  const [selectedTime, setSelectedTime] = useState<string>(() => saved?.time || "");
  const [confirmedTime, setConfirmedTime] = useState<string>("");
  const customerStorageKey = `shop-customer-${shopId}`;
  const getSavedCustomer = () => {
    try {
      const raw = localStorage.getItem(customerStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const savedCustomer = useMemo(getSavedCustomer, [customerStorageKey]);

  const [formData, setFormData] = useState<Record<string, string>>(() => {
    if (saved?.formData) return saved.formData;
    return {
      firstName: savedCustomer?.firstName || "",
      lastName: savedCustomer?.lastName || "",
      email: savedCustomer?.email || "",
      phone: "",
    };
  });
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>(() => saved?.customFields || {});

  // Restore selected service once services are loaded
  useEffect(() => {
    if (pendingServiceId && services.length > 0) {
      const svc = services.find((s) => s.id === pendingServiceId);
      if (svc) setSelectedService(svc);
      setPendingServiceId(null);
    }
  }, [services, pendingServiceId]);

  // Fetch upsells when a service is selected
  useEffect(() => {
    if (!selectedService || !shopId) {
      setServiceUpsells([]);
      return;
    }
    const fetchUpsells = async () => {
      const { data: links } = await supabase
        .from("service_upsells")
        .select("upsell_id, sort_order")
        .eq("service_id", selectedService.id)
        .order("sort_order", { ascending: true });
      if (!links || links.length === 0) {
        setServiceUpsells([]);
        return;
      }
      const upsellIds = links.map((l: any) => l.upsell_id);
      const { data: upsells } = await supabase
        .from("upsells")
        .select("id, name, required, option_selection_mode, upsell_options(id, name, price, duration)")
        .in("id", upsellIds);
      if (!upsells) {
        setServiceUpsells([]);
        return;
      }
      // Sort by the order from service_upsells
      const ordered = upsellIds
        .map((id: string) => upsells.find((u: any) => u.id === id))
        .filter(Boolean)
        .map((u: any) => ({
          id: u.id,
          name: u.name,
          required: u.required,
          option_selection_mode: u.option_selection_mode || "single",
          options: (u.upsell_options || []).map((o: any) => ({
            id: o.id,
            name: o.name,
            price: Number(o.price),
            duration: o.duration,
          })),
        }));
      setServiceUpsells(ordered);
    };
    fetchUpsells();
  }, [selectedService?.id, shopId]);

  // Save state to sessionStorage on changes (never persist confirmation step)
  useEffect(() => {
    if (loading || step === STEP_CONFIRMED) return;
    const state = {
      step,
      serviceId: selectedService?.id || null,
      options: selectedOptions,
      upsellOptions: selectedUpsellOptions,
      date: selectedDate?.toISOString() || null,
      time: selectedTime,
      formData,
      customFields: customFieldValues,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(state));
  }, [step, selectedService, selectedOptions, selectedUpsellOptions, selectedDate, selectedTime, formData, customFieldValues, loading, storageKey, STEP_CONFIRMED]);

  // Auto-select first available date when data loads or when current date becomes unavailable
  useEffect(() => {
    if (loading || notFound || openingHours.length === 0) return;
    // If no date selected, or the selected date is now disabled, pick the first available
    if (!selectedDate || (selectedService && isDateDisabled(selectedDate))) {
      const first = getFirstAvailableDate();
      setSelectedDate(first);
    }
  }, [loading, notFound, openingHours, blockedTimes, selectedService, selectedOptions, existingReservations]);

  useEffect(() => {
    if (organisation?.name) {
      document.title = `${organisation.name} — ${t("shop.title")}`;
    }
  }, [organisation?.name, language]);

  // All selected upsell option IDs flattened
  const allUpsellOptionIds = useMemo(() => {
    return Object.values(selectedUpsellOptions).flat();
  }, [selectedUpsellOptions]);

  const totalDuration = useMemo(() => {
    if (!selectedService) return 0;
    const optDuration = selectedService.options
      .filter((o) => selectedOptions.includes(o.id))
      .reduce((acc, o) => acc + o.duration, 0);
    const upsellDuration = serviceUpsells
      .flatMap((u) => u.options)
      .filter((o) => allUpsellOptionIds.includes(o.id))
      .reduce((acc, o) => acc + o.duration, 0);
    return selectedService.duration + optDuration + upsellDuration;
  }, [selectedService, selectedOptions, serviceUpsells, allUpsellOptionIds]);

  const totalPrice = useMemo(() => {
    if (!selectedService) return 0;
    const optPrice = selectedService.options
      .filter((o) => selectedOptions.includes(o.id))
      .reduce((acc, o) => acc + o.price, 0);
    const upsellPrice = serviceUpsells
      .flatMap((u) => u.options)
      .filter((o) => allUpsellOptionIds.includes(o.id))
      .reduce((acc, o) => acc + o.price, 0);
    return selectedService.price + optPrice + upsellPrice;
  }, [selectedService, selectedOptions, serviceUpsells, allUpsellOptionIds]);

  const timeSlots = useMemo(() => {
    if (!selectedDate) return [];
    // Normalize to local midnight before computing slots
    const localDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    return getAvailableSlots(localDate, totalDuration);
  }, [totalDuration, selectedDate, openingHours, blockedTimes, existingReservations, slotLocks, employeesEnabled, selectedEmployee, selectedService, shopEmployees, employeeServiceMap, employeeWorkingHours]);

  useEffect(() => {
    // Never auto-select once user has moved past the datetime step
    if (step > STEP_DATETIME) return;
    if (timeSlots.length > 0 && selectedDate) {
      // Only auto-select the first slot if no slot is selected yet,
      // or if the currently selected slot is no longer available
      if (!selectedTime || !timeSlots.includes(selectedTime)) {
        setSelectedTime(timeSlots[0]);
        // Note: we don't lock on auto-select — lock happens when user clicks Continue
      }
    }
  }, [selectedDate, timeSlots, step, STEP_DATETIME]);

  // Select a timeslot (no locking — lock happens on Continue)
  const handleSelectTime = useCallback((slot: string) => {
    setSelectedTime(slot);
  }, []);

  const releaseLock = useCallback(async () => {
    if (!lockToken || !shopId) return;
    try {
      await supabase.functions.invoke("lock-slot", {
        body: { action: "release", lock_token: lockToken },
      });
    } catch (err) {
      // Lock will expire naturally, ignore errors
    }
    setLockToken(null);
  }, [lockToken, shopId]);

  const toggleOption = (optId: string) => {
    if (selectedService?.option_selection_mode === "single") {
      setSelectedOptions((prev) =>
        prev.includes(optId) ? [] : [optId]
      );
    } else {
      setSelectedOptions((prev) =>
        prev.includes(optId) ? prev.filter((id) => id !== optId) : [...prev, optId]
      );
    }
  };

  const toggleUpsellOption = (upsellId: string, optId: string, mode: string) => {
    setSelectedUpsellOptions((prev) => {
      const current = prev[upsellId] || [];
      if (mode === "single") {
        return { ...prev, [upsellId]: current.includes(optId) ? [] : [optId] };
      }
      return {
        ...prev,
        [upsellId]: current.includes(optId)
          ? current.filter((id) => id !== optId)
          : [...current, optId],
      };
    });
  };

  const [booking, setBooking] = useState(false);
  const [reservationNumber, setReservationNumber] = useState<string | null>(null);

  const handleBook = async () => {
    if (!selectedService || !selectedDate || !selectedTime || !shopId) return;
    const customerName = `${formData.firstName} ${formData.lastName}`.trim();
    if (!customerName || !formData.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setBooking(true);

    try {
      // Build the date-time
      const [h, m] = selectedTime.split(":").map(Number);
      const bookingDate = new Date(selectedDate);
      bookingDate.setHours(h, m, 0, 0);

      // Build custom fields with labels for the note
      const customFieldsWithLabels: Record<string, string> = {};
      checkoutFields.forEach((f) => {
        if (customFieldValues[f.id]) {
          customFieldsWithLabels[f.label] = customFieldValues[f.id];
        }
      });

      const { data, error } = await supabase.functions.invoke("create-booking", {
        body: {
          organisation_id: shopId,
          service_id: selectedService.id,
          date: bookingDate.toISOString(),
          duration: totalDuration,
          price: totalPrice,
          selected_options: [...selectedOptions, ...allUpsellOptionIds],
          customer_name: customerName,
          customer_email: formData.email,
          customer_phone: formData.phone || "",
          custom_fields: customFieldsWithLabels,
          language,
          time: selectedTime,
          pay_at_order: payAtOrderEnabled,
          lock_token: lockToken,
          employee_id: selectedEmployee || null,
        },
      });

      console.log("create-booking response:", { data, error });

      if (error || data?.error) {
        throw new Error(data?.error || error?.message || "Booking failed");
      }

      // Persist customer credentials for next visit
      localStorage.setItem(customerStorageKey, JSON.stringify({ firstName: formData.firstName, lastName: formData.lastName, email: formData.email }));
      sessionStorage.removeItem(storageKey);

      // If pay-at-order is enabled and order is not free, redirect to payment
      if (payAtOrderEnabled && totalPrice > 0 && data?.reservation_id) {
        const returnUrl = `${window.location.origin}/shop/${shopId}/payment`;
        const { data: paymentData, error: payError } = await supabase.functions.invoke("create-payment", {
          body: {
            reservation_id: data.reservation_id,
            organisation_id: shopId,
            return_url: returnUrl,
          },
        });

        console.log("create-payment response:", { paymentData, payError });

        if (payError || paymentData?.error) {
          throw new Error(paymentData?.error || payError?.message || "Payment creation failed");
        }

        if (paymentData?.checkout_url) {
          window.location.href = paymentData.checkout_url;
          return;
        }
      }

      setReservationNumber(data?.reservation_number || null);
      // Snapshot the confirmed time before refreshing availability (which may overwrite selectedTime)
      setConfirmedTime(selectedTime);
      setLockToken(null);
      // Immediately refresh availability so re-booking reflects this new reservation
      await fetchAvailability();
      setStep(STEP_CONFIRMED);
    } catch (err: any) {
      const msg = err.message || "";
      if (msg.toLowerCase().includes("no longer available") || msg.toLowerCase().includes("currently being booked")) {
        toast.error(t("shop.slot_taken"));
        await fetchAvailability();
        setSelectedTime("");
        setStep(STEP_DATETIME);
      } else if (msg.toLowerCase().includes("too many")) {
        toast.error(t("shop.rate_limit_error"));
      } else {
        toast.error(msg || t("shop.booking_error"));
      }
    } finally {
      setBooking(false);
    }
  };

  const thankYouText = t("shop.thank_you")
    .replace("{name}", formData.firstName)
    .replace("{email}", formData.email);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Shop not found</h1>
          <p className="text-muted-foreground">This booking page doesn't exist.</p>
        </div>
      </div>
    );
  }

  if (shopUnavailable) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">{t("shop.unavailable_title")}</h1>
          <p className="text-muted-foreground">{t("shop.unavailable_description")}</p>
        </div>
      </div>
    );
  }

  // Build custom style overrides
  const isDark = resolvedTheme === "dark";
  const customPrimary = isDark ? organisation?.shop_primary_color_dark : organisation?.shop_primary_color;
  const customBtnText = isDark ? organisation?.shop_button_text_color_dark : organisation?.shop_button_text_color;
  const customText = isDark ? organisation?.shop_text_color_dark : organisation?.shop_text_color;
  const customBg = isDark ? organisation?.shop_bg_color_dark : organisation?.shop_bg_color;
  const customCardBg = isDark ? organisation?.shop_card_bg_color_dark : organisation?.shop_card_bg_color;
  const customFontFamily = organisation?.shop_font_family || "Inter";
  const customFontSize = organisation?.shop_font_size === "small" ? "14px" : organisation?.shop_font_size === "large" ? "18px" : "16px";

  const customStyles: React.CSSProperties = {
    fontFamily: `"${customFontFamily}", sans-serif`,
    fontSize: customFontSize,
    ...(customBg ? { backgroundColor: customBg } : {}),
    ...(customText ? { color: customText } : {}),
  };

  const hasCustomStyles = customPrimary || customCardBg;


  return (
    <div className={cn("min-h-screen bg-background", !isEmbed && "py-8")} style={customStyles} data-shop-custom={hasCustomStyles ? "true" : undefined} role="main" aria-label="Booking page">
      {hasCustomStyles && (() => {
        const btnFg = customBtnText || (customPrimary ? bestForeground(customPrimary) : "#fff");
        return (
          <style>{`
            ${customPrimary ? `
            [data-shop-custom] .bg-primary { background-color: ${customPrimary} !important; }
            [data-shop-custom] .bg-primary\\/5 { background-color: ${customPrimary}0d !important; }
            [data-shop-custom] .text-primary { color: ${customPrimary} !important; }
            [data-shop-custom] .border-primary { border-color: ${customPrimary} !important; }
            [data-shop-custom] .ring-primary\\/20 { --tw-ring-color: ${customPrimary}33 !important; }
            [data-shop-custom] .hover\\:border-primary\\/30:hover { border-color: ${customPrimary}4d !important; }
            [data-shop-custom] .hover\\:bg-primary\\/5:hover { background-color: ${customPrimary}0d !important; }
            [data-shop-custom] .hover\\:bg-primary:hover { background-color: ${customPrimary} !important; }
            [data-shop-custom] .focus\\:bg-primary:focus { background-color: ${customPrimary} !important; }
            [data-shop-custom] .shop-btn-primary { background-color: ${customPrimary} !important; border-color: ${customPrimary} !important; color: ${btnFg} !important; }
            [data-shop-custom] .shop-btn-primary:hover { opacity: 0.9; }
            [data-shop-custom] .shop-btn-primary:focus-visible { outline: 2px solid ${customPrimary} !important; outline-offset: 2px; }
            [data-shop-custom] [data-state="checked"] { background-color: ${customPrimary} !important; border-color: ${customPrimary} !important; }
            [data-shop-custom] .shop-step-active { background-color: ${customPrimary} !important; color: ${btnFg} !important; }
            [data-shop-custom] .shop-step-line-active { background-color: ${customPrimary} !important; }
            [data-shop-custom] .rdp-day_selected,
            [data-shop-custom] .rdp-day_selected:hover,
            [data-shop-custom] .rdp-day_selected:focus { background-color: ${customPrimary} !important; color: ${btnFg} !important; }
            ` : ""}
            ${customCardBg ? `
            [data-shop-custom] .shop-card { background-color: ${customCardBg} !important; }
            ` : ""}
          `}</style>
        );
      })()}
      <div className="mx-auto max-w-2xl px-4">
        {/* Header */}
        {!isEmbed && (
          <div className="mb-8">
            <div className="flex items-start justify-end gap-1 mb-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                aria-label={resolvedTheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              >
                {resolvedTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <LanguageSwitcher />
            </div>
            <div className="text-center">
              {(() => {
                const logoUrl = resolvedTheme === "dark" ? organisation?.logo_dark_url : organisation?.logo_light_url;
                const sizeClass = { small: "h-8", medium: "h-12", large: "h-16", xlarge: "h-20" }[organisation?.logo_size || "medium"] || "h-12";
                return logoUrl ? (
                  <div className="mx-auto mb-3">
                    <img src={logoUrl} alt={organisation?.name || "Logo"} className={`${sizeClass} mx-auto max-w-[280px] object-contain`} />
                  </div>
                ) : (
                  <h1 className="text-2xl font-bold text-foreground">{organisation?.name}</h1>
                );
              })()}
              <p className="mt-1 text-sm text-muted-foreground">{t("shop.title")}</p>
            </div>
          </div>
        )}

        {/* Embed language switcher */}
        {isEmbed && (
          <div className="mb-4 flex justify-end">
            <LanguageSwitcher />
          </div>
        )}

        {/* Progress Steps */}
        <div ref={stepsContainerRef} className="mb-8 flex items-center gap-2 overflow-x-auto px-2 scrollbar-hide mx-auto w-fit max-w-full">
          {steps.map((s, i) => (
            <div key={s.id} ref={step === s.id ? activeStepRef : undefined} className="flex items-center gap-2 shrink-0">
              <div
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all",
                  step >= s.id
                    ? "bg-primary text-primary-foreground shop-step-active"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {step > s.id ? <Check className="h-4 w-4" /> : s.id}
              </div>
              <span className={cn("text-xs font-medium whitespace-nowrap", step >= s.id ? "text-foreground" : "text-muted-foreground")}>
                {s.label}
              </span>
              {i < steps.length - 1 && <div className={cn("mx-2 h-0.5 w-8 shrink-0", step > s.id ? "bg-primary shop-step-line-active" : "bg-border")} />}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card shop-card">
          <AnimatePresence mode="wait">
            {step === STEP_SERVICE && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <h2 className="text-lg font-semibold text-card-foreground mb-4">{t("shop.choose_service")}</h2>
                {displayedServices.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No services available yet.</p>
                ) : (
                  <div className="space-y-3" role="radiogroup" aria-label="Available services">
                    {displayedServices.map((service) => (
                      <div key={service.id}>
                        <button
                          onClick={() => {
                            setSelectedService(service);
                            setSelectedOptions([]);
                            setSelectedUpsellOptions({});
                            if (!empBefore) setSelectedEmployee(null);
                          }}
                          aria-pressed={selectedService?.id === service.id}
                          aria-label={`${service.name} — €${service.price.toFixed(2)}, ${service.duration} minutes`}
                          className={cn(
                            "w-full rounded-xl border p-4 text-left transition-all",
                            selectedService?.id === service.id
                              ? "border-primary bg-primary/5 ring-1 ring-primary/20 shop-primary-border shop-ring-primary"
                              : "border-border hover:border-primary/30 hover:bg-muted/50"
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-semibold text-card-foreground">{service.name}</h3>
                              <p className="text-xs text-muted-foreground mt-0.5">{service.description}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-primary shop-primary-text">€{service.price.toFixed(2)}</p>
                              <p className="text-[10px] text-muted-foreground">{service.duration} min</p>
                            </div>
                          </div>
                        </button>

                        {selectedService?.id === service.id && service.options.length > 0 && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            className="ml-4 mt-3 space-y-2"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <p className={cn(
                                "text-xs font-semibold",
                                service.option_required ? "text-foreground" : "text-muted-foreground"
                              )}>
                                {service.option_required ? t("shop.required_options") : t("shop.add_options")}
                              </p>
                              {service.option_required && selectedOptions.length === 0 && (
                                <span className="text-[10px] font-medium text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">!</span>
                              )}
                            </div>
                            {service.options.map((opt) => (
                              <button
                                key={opt.id}
                                onClick={() => toggleOption(opt.id)}
                                className={cn(
                                  "flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-all text-sm",
                                  selectedOptions.includes(opt.id)
                                    ? "border-primary bg-primary/5 ring-1 ring-primary/20 shop-primary-border shop-ring-primary"
                                    : "border-border hover:border-primary/30"
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={cn(
                                    "flex h-5 w-5 items-center justify-center rounded transition-colors",
                                    service.option_selection_mode === "single" ? "rounded-full border-2" : "rounded border",
                                    selectedOptions.includes(opt.id) ? "bg-primary border-primary text-primary-foreground shop-primary-bg shop-primary-border" : "border-muted-foreground/30"
                                  )}>
                                    {selectedOptions.includes(opt.id) && <Check className="h-3 w-3" />}
                                  </div>
                                  <span className="text-card-foreground font-medium">{opt.name}</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs">
                                  {opt.price > 0 && (
                                    <span className="flex items-center gap-0.5 font-semibold text-primary shop-primary-text">
                                      <Euro className="h-3 w-3" />+{opt.price.toFixed(2)}
                                    </span>
                                  )}
                                  {opt.duration > 0 && (
                                    <span className="flex items-center gap-0.5 text-muted-foreground">
                                      <Clock className="h-3 w-3" />+{opt.duration} min
                                    </span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {selectedService && (
                  <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
                    {empBefore ? (
                      <Button variant="ghost" onClick={() => setStep(STEP_EMPLOYEE)}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {t("shop.back")}
                      </Button>
                    ) : (
                      <div />
                    )}
                    <div className="flex items-center gap-3">
                      <p className="text-sm text-muted-foreground">{t("shop.total")}: <span className="font-bold text-foreground">€{totalPrice.toFixed(2)}</span> · {totalDuration} min</p>
                      <Button
                        className="shop-btn-primary"
                        onClick={() => {
                          const nextAfterService = empBefore
                            ? (upsellCount > 0 ? upsellBase : STEP_DATETIME)
                            : (hasEmployeeStep ? STEP_EMPLOYEE : (upsellCount > 0 ? upsellBase : STEP_DATETIME));
                          setStep(nextAfterService);
                        }}
                        disabled={selectedService.option_required && selectedService.options.length > 0 && selectedOptions.length === 0}
                      >
                        {t("shop.continue")}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
                {selfServiceEnabled && (
                  <div className="mt-4 text-center">
                    <Link
                      to={`/shop/${shopId}/manage`}
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Settings2 className="h-3.5 w-3.5" />
                      {t("ext.ss_manage_link")}
                    </Link>
                  </div>
                )}
              </motion.div>
            )}

            {/* Employee Step */}
            {hasEmployeeStep && step === STEP_EMPLOYEE && (
              <motion.div key="step-employee" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <h2 className="text-lg font-semibold text-card-foreground mb-4">{t("shop.choose_employee")}</h2>
                <div className="space-y-3">
                  {allowNoPreference && (
                    <button
                      onClick={() => setSelectedEmployee(null)}
                      className={cn(
                        "w-full rounded-xl border p-4 text-left transition-all",
                        selectedEmployee === null
                          ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                          : "border-border hover:border-primary/30 hover:bg-muted/50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                          <UserCheck className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-card-foreground">{t("shop.no_preference")}</h3>
                          <p className="text-xs text-muted-foreground">{t("shop.no_preference_desc")}</p>
                        </div>
                      </div>
                    </button>
                  )}
                  {(empBefore ? shopEmployees : eligibleEmployees).map((emp) => (
                    <button
                      key={emp.id}
                      onClick={() => setSelectedEmployee(emp.id)}
                      className={cn(
                        "w-full rounded-xl border p-4 text-left transition-all",
                        selectedEmployee === emp.id
                          ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                          : "border-border hover:border-primary/30 hover:bg-muted/50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {emp.image_url ? (
                          <img src={emp.image_url} alt={emp.name} className="h-10 w-10 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                            {emp.name.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <h3 className="text-sm font-semibold text-card-foreground">{emp.name}</h3>
                      </div>
                    </button>
                  ))}
                  {(empBefore ? shopEmployees : eligibleEmployees).length === 0 && !allowNoPreference && (
                    <p className="text-sm text-muted-foreground py-4 text-center">{t("shop.no_employees_available")}</p>
                  )}
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
                  {empBefore ? <div /> : (
                    <Button variant="ghost" onClick={() => setStep(STEP_SERVICE)}>
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      {t("shop.back")}
                    </Button>
                  )}
                  <Button
                    className="shop-btn-primary"
                    onClick={() => setStep(empBefore ? STEP_SERVICE : STEP_EMPLOYEE + 1)}
                    disabled={!allowNoPreference && !selectedEmployee && (empBefore ? shopEmployees : eligibleEmployees).length > 0}
                  >
                    {t("shop.continue")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Upsell Steps */}
            {serviceUpsells.map((upsell, uIdx) => {
              const upsellStepId = upsellBase + uIdx;
              const selected = selectedUpsellOptions[upsell.id] || [];
              return step === upsellStepId && (
                <motion.div key={`upsell-${upsell.id}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-card-foreground">{upsell.name}</h2>
                    <span className={cn(
                      "text-[10px] font-medium px-2 py-0.5 rounded-full",
                      upsell.required
                        ? "bg-destructive/10 text-destructive"
                        : "bg-muted text-muted-foreground"
                    )}>
                      {upsell.required ? t("shop.upsell_required") : t("shop.upsell_optional")}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {upsell.options.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => toggleUpsellOption(upsell.id, opt.id, upsell.option_selection_mode)}
                        className={cn(
                          "flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-all text-sm",
                          selected.includes(opt.id)
                            ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                            : "border-border hover:border-primary/30"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "flex h-5 w-5 items-center justify-center rounded transition-colors",
                            upsell.option_selection_mode === "single" ? "rounded-full border-2" : "rounded border",
                            selected.includes(opt.id) ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/30"
                          )}>
                            {selected.includes(opt.id) && <Check className="h-3 w-3" />}
                          </div>
                          <span className="text-card-foreground font-medium">{opt.name}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          {opt.price > 0 && (
                            <span className="flex items-center gap-0.5 font-semibold text-primary">
                              <Euro className="h-3 w-3" />+{opt.price.toFixed(2)}
                            </span>
                          )}
                          {opt.duration > 0 && (
                            <span className="flex items-center gap-0.5 text-muted-foreground">
                              <Clock className="h-3 w-3" />+{opt.duration} min
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
                    <Button variant="ghost" onClick={() => setStep(upsellStepId - 1)}>
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      {t("shop.back")}
                    </Button>
                    <div className="flex items-center gap-2">
                      {!upsell.required && selected.length === 0 && (
                        <Button variant="ghost" onClick={() => setStep(upsellStepId + 1)}>
                          {t("shop.upsell_skip")}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        className="shop-btn-primary"
                        onClick={() => setStep(upsellStepId + 1)}
                        disabled={upsell.required && selected.length === 0}
                      >
                        {t("shop.continue")}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {step === STEP_DATETIME && (
              <motion.div key="step-datetime" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <h2 className="text-lg font-semibold text-card-foreground mb-4">{t("shop.pick_datetime")}</h2>
                <div className="flex flex-col gap-6 md:flex-row">
                  <div className="flex-1">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => {
                        setSelectedDate(d);
                        setSelectedTime("");
                      }}
                      disabled={isDateDisabled}
                      weekStartsOn={1}
                      className="p-3 pointer-events-auto rounded-xl border border-border"
                      classNames={{
                        day_today: "border-2 border-primary/60 font-semibold text-foreground bg-transparent",
                      }}
                      initialFocus
                    />
                  </div>
                  {selectedDate ? (
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground mb-3">
                        {t("shop.available_times")} {format(selectedDate, "EEEE, MMM d")}
                      </p>
                      {timeSlots.length === 0 ? (
                        <div className="flex items-center justify-center rounded-xl border border-dashed border-border py-8 px-4">
                          <p className="text-sm text-muted-foreground text-center">{t("shop.no_timeslots")}</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Available time slots">
                          {timeSlots.map((slot) => (
                            <button
                              key={slot}
                              onClick={() => handleSelectTime(slot)}
                              aria-pressed={selectedTime === slot}
                              aria-label={`Time slot ${slot}`}
                              className={cn(
                                "rounded-lg border px-3 py-2 text-sm font-medium transition-all",
                                selectedTime === slot
                                  ? "border-primary bg-primary text-primary-foreground shop-step-active"
                                  : "border-border text-foreground hover:border-primary/30 hover:bg-primary/5"
                              )}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center rounded-xl border border-dashed border-border py-8 px-4">
                      <p className="text-sm text-muted-foreground text-center">{t("shop.no_timeslots")}</p>
                    </div>
                  )}
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
                  <Button variant="ghost" onClick={() => { releaseLock(); setStep(STEP_DATETIME - 1); }}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t("shop.back")}
                  </Button>
                  <Button className="shop-btn-primary" onClick={async () => {
                    if (!selectedDate || !selectedTime || !shopId) return;
                    // Lock the slot before proceeding
                    const [h, m] = selectedTime.split(":").map(Number);
                    const slotDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), h, m, 0, 0);
                    try {
                      const { data, error } = await supabase.functions.invoke("lock-slot", {
                        body: {
                          organisation_id: shopId,
                          slot_date: slotDate.toISOString(),
                          duration: totalDuration,
                          previous_lock_token: lockToken,
                        },
                      });
                      if (data?.error === "slot_taken") {
                        toast.error(t("shop.slot_taken") || "This timeslot is no longer available.");
                        await fetchAvailability();
                        setSelectedTime("");
                        return;
                      }
                      if (data?.lock_token) {
                        setLockToken(data.lock_token);
                      }
                    } catch (err) {
                      console.warn("Slot lock failed:", err);
                    }
                    setStep(STEP_DETAILS);
                  }} disabled={!selectedDate || !selectedTime}>
                    {t("shop.continue")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === STEP_DETAILS && (
              <motion.div key="step-details" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <h2 className="text-lg font-semibold text-card-foreground mb-4">{t("shop.your_details")}</h2>

                {/* Summary */}
                <div className="mb-6 rounded-xl bg-muted/50 p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t("shop.service")}</span>
                    <span className="font-medium text-foreground">{selectedService?.name}</span>
                  </div>
                  {selectedOptions.length > 0 && (
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-muted-foreground">{t("shop.options")}</span>
                      <span className="font-medium text-foreground">
                        {selectedService?.options.filter((o) => selectedOptions.includes(o.id)).map((o) => o.name).join(", ")}
                      </span>
                    </div>
                  )}
                  {serviceUpsells.map((u) => {
                    const sel = selectedUpsellOptions[u.id] || [];
                    if (sel.length === 0) return null;
                    return (
                      <div key={u.id} className="flex items-center justify-between text-sm mt-1">
                        <span className="text-muted-foreground">{u.name}</span>
                        <span className="font-medium text-foreground">
                          {u.options.filter((o) => sel.includes(o.id)).map((o) => o.name).join(", ")}
                        </span>
                      </div>
                    );
                  })}
                  {employeesEnabled && (
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-muted-foreground">{t("shop.step.employee")}</span>
                      <span className="font-medium text-foreground">
                        {selectedEmployee
                          ? shopEmployees.find(e => e.id === selectedEmployee)?.name || "—"
                          : t("shop.no_preference")}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-muted-foreground">{t("shop.date_time")}</span>
                    <span className="font-medium text-foreground">
                      {selectedDate && format(selectedDate, "MMM d, yyyy")} at {selectedTime}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1 border-t border-border pt-2 mt-2">
                    <span className="font-medium text-foreground">{t("shop.total")}</span>
                    <span className="font-bold text-primary shop-primary-text">€{totalPrice.toFixed(2)} · {totalDuration} min</span>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t("shop.first_name")} *</Label>
                    <Input
                      value={formData.firstName}
                      onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("shop.last_name")} *</Label>
                    <Input
                      value={formData.lastName}
                      onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("shop.email")} *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                      required
                    />
                  </div>

                  {/* Dynamic checkout fields from config */}
                  {checkoutFields.map((field) => (
                    <div key={field.id} className={cn("space-y-2", field.type === "textarea" ? "md:col-span-2" : "")}>
                      <Label>{field.label}{field.required ? " *" : ""}</Label>
                      {field.type === "textarea" ? (
                        <Textarea
                          value={customFieldValues[field.id] || ""}
                          onChange={(e) => setCustomFieldValues((p) => ({ ...p, [field.id]: e.target.value }))}
                          placeholder={field.placeholder}
                          required={field.required}
                        />
                      ) : field.type === "checkbox" ? (
                        <div className="flex items-center gap-2 pt-1">
                          <Checkbox
                            checked={customFieldValues[field.id] === "true"}
                            onCheckedChange={(v) => setCustomFieldValues((p) => ({ ...p, [field.id]: v ? "true" : "" }))}
                          />
                          <span className="text-sm text-muted-foreground">{field.placeholder}</span>
                        </div>
                      ) : field.type === "select" ? (
                        <Select
                          value={customFieldValues[field.id] || ""}
                          onValueChange={(v) => setCustomFieldValues((p) => ({ ...p, [field.id]: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={field.placeholder || "Select..."} />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options.split(",").map((opt) => opt.trim()).filter(Boolean).map((opt) => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type={field.type === "phone" ? "tel" : field.type === "number" ? "number" : field.type === "email" ? "email" : "text"}
                          value={customFieldValues[field.id] || ""}
                          onChange={(e) => setCustomFieldValues((p) => ({ ...p, [field.id]: e.target.value }))}
                          placeholder={field.placeholder}
                          required={field.required}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
                  <Button variant="ghost" onClick={() => { releaseLock(); setStep(STEP_DATETIME); }}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t("shop.back")}
                  </Button>
                  <Button
                    className="shop-btn-primary"
                    onClick={handleBook}
                    disabled={
                      booking ||
                      !formData.firstName || !formData.lastName || !formData.email ||
                      checkoutFields.some((f) => f.required && !customFieldValues[f.id]?.trim())
                    }
                  >
                    {booking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {payAtOrderEnabled ? t("shop.pay_and_book") : t("shop.confirm_booking")}
                    <CheckCircle2 className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === STEP_CONFIRMED && (
              <motion.div key="step-confirmed" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} className="text-center py-8">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
                  <CheckCircle2 className="h-8 w-8 text-accent" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">{t("shop.booking_confirmed")}</h2>
                {reservationNumber && (
                  <p className="mt-1 font-mono text-sm text-muted-foreground">{reservationNumber}</p>
                )}
                <p className="mt-2 text-muted-foreground">{thankYouText}</p>
                <div className="mx-auto mt-6 max-w-sm rounded-xl bg-muted/50 p-4 text-left">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("shop.service")}</span>
                      <span className="font-medium text-foreground">{selectedService?.name}</span>
                    </div>
                    {employeesEnabled && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t("shop.step.employee")}</span>
                        <span className="font-medium text-foreground">
                          {selectedEmployee
                            ? shopEmployees.find(e => e.id === selectedEmployee)?.name || "—"
                            : t("shop.no_preference")}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("shop.date_time")}</span>
                      <span className="font-medium text-foreground">{selectedDate && format(selectedDate, "EEEE, MMM d, yyyy")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Time</span>
                      <span className="font-medium text-foreground">{confirmedTime || selectedTime}</span>
                    </div>
                    <div className="flex justify-between border-t border-border pt-2">
                      <span className="font-medium text-foreground">{t("shop.total")}</span>
                      <span className="font-bold text-primary shop-primary-text">€{totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <p className="mt-6 text-xs text-muted-foreground">{t("shop.reminder")}</p>
                {selectedDate && (confirmedTime || selectedTime) && selectedService && (
                  <AddToCalendar
                    title={`${selectedService.name}${employeesEnabled && selectedEmployee ? ` · ${shopEmployees.find(e => e.id === selectedEmployee)?.name || ""}` : ""}${organisation?.name ? ` — ${organisation.name}` : ""}`}
                    date={selectedDate}
                    time={confirmedTime || selectedTime}
                    durationMinutes={totalDuration}
                    location={organisation?.name}
                  />
                )}
                <Button
                  className="mt-6 shop-btn-primary"
                  onClick={() => {
                    sessionStorage.removeItem(storageKey);
                    fetchAvailability();
                    setStep(1);
                    setSelectedService(null);
                    setPendingServiceId(null);
                    setSelectedOptions([]);
                    setSelectedUpsellOptions({});
                    setSelectedEmployee(null);
                    setSelectedDate(undefined);
                    setSelectedTime("");
                    setFormData({ firstName: "", lastName: "", email: "", phone: "" });
                    setCustomFieldValues({});
                    setReservationNumber(null);
                  }}
                >
                  {t("shop.book_another") || "Book another appointment"}
                </Button>
                {selfServiceEnabled && selfServiceThankYou && (
                  <div className="mt-3">
                    <Link
                      to={`/shop/${shopId}/manage`}
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Settings2 className="h-3.5 w-3.5" />
                      {t("ext.ss_manage_link")}
                    </Link>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Powered by Slotify footer */}
        {!isEmbed && (
          <div className="mt-8 text-center">
            <a
              href="https://slotify.nl"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
            >
              Powered by
              <img src={slotifyLogoSrc} alt="Slotify" className="h-3.5" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopBooking;
